import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'

import { CookieBanner } from '@/components/common/CookieBanner'
import { NotFoundState } from '@/components/common/NotFoundState'
import { SessionEnded } from '@/components/common/SessionEnded'
import { ErrorState } from '@/components/common/StateBlocks'
import { AppToaster } from '@/components/layout/AppToaster'
import { MaintenanceGate } from '@ventawiz/web-kit'

/**
 * The root route.
 *
 * `createRootRouteWithContext` types the router's context, so every route's
 * loader gets the QueryClient without reaching for a module singleton — which
 * is what lets a test render a route against its own cache.
 */
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  errorComponent: RootError,
  notFoundComponent: NotFoundState,
})

function RootLayout() {
  return (
    <>
      {/* First, so it is early in the reading order. Fixed to the bottom of
          the viewport and non-modal: it never blocks the page — and outside
          the maintenance gate, so "Cookie settings" works on that page too. */}
      <CookieBanner />
      {/* The maintenance page replaces the whole app while a window is open,
          at the root so it covers every page, and reloads by itself when the
          window ends. The banner and the operator bar are not rendered here:
          the shell's sidebar is fixed and would sit on top of them, so
          AppShell places them in its main column. */}
      <MaintenanceGate notices={false}>
        <Outlet />
      </MaintenanceGate>
      {/* Outside the shell deliberately. Login has no sidebar and still needs
          toasts, and inside AppShell this rendered within the scrolling
          <main>, which is not where a fixed overlay belongs. No
          `richColors`: a toast is a surface card with a tone icon, never a
          tinted fill. */}
      {/* Bottom-left, away from the AI Analyst launcher; bottom-right when it is parked there. */}
      <AppToaster />
      {/* "Your session ended", for the moment between a session ending
          mid-page (a refused refresh, a sign-out in another tab) and the kit
          sending the browser to sign in. Last, so it paints over everything. */}
      <SessionEnded />
    </>
  )
}

/**
 * The last line of defence.
 *
 * Without an error boundary at the root, a render-time throw anywhere in the
 * tree unmounts the whole application and the user sees a white page with no
 * way back.
 */
function RootError({ error, reset }: { error: unknown; reset: () => void }) {
  return (
    <div className="mx-auto max-w-lg py-16">
      <ErrorState error={error} onRetry={reset} />
    </div>
  )
}
